import React, { useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { AppHeader } from './AppHeader';

/**
 * Page frame for signed-in pages: skip link, sticky header and <main>.
 * Publishes the header height as --header-h so sticky side panels sit below it.
 * Used as a layout route (renders <Outlet />) or with explicit children.
 */
export const AppShell = ({ children, header = <AppHeader /> }) => {
  const frameRef = useRef(null);

  useEffect(() => {
    const headerEl = frameRef.current?.querySelector('[data-app-header]');
    if (!headerEl || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => {
      document.documentElement.style.setProperty('--header-h', `${headerEl.offsetHeight}px`);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(headerEl);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={frameRef} className="min-h-screen bg-canvas">
      <a
        href="#main"
        onClick={(e) => {
          // Move focus directly instead of changing the URL hash, which the router would record as a navigation.
          e.preventDefault();
          document.getElementById('main')?.focus();
        }}
        className="sr-only z-50 rounded-lg bg-accent-solid px-4 py-2 text-sm font-medium text-on-accent focus:not-sr-only focus:fixed focus:left-4 focus:top-3"
      >
        Skip to content
      </a>
      {header}
      <main id="main" tabIndex={-1} className="focus:outline-none">
        {children || <Outlet />}
      </main>
    </div>
  );
};
